/**
 * Item Frame Selector
 * 
 * Allows switching between different frames (problem, opportunity, etc.)
 * of the same item. Shows all available frames and indicates the canonical one.
 */

import React, { useMemo } from 'react';
import { 
  RefreshCw, 
  AlertCircle, 
  Lightbulb, 
  Shield, 
  Hammer, 
  Handshake, 
  TrendingUp,
  Check,
  Plus,
} from 'lucide-react';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { CATEGORY_BADGE } from '../../../lib/itemCategoryColors';
import type { CandidateLanguageType } from '../../../types/prioritization/lifecycle';

interface ItemFrameSelectorProps {
  candidateId: string;
  onCreateFrame?: () => void;
  onEditFrame?: (frameId: string) => void;
  compact?: boolean;
}

const FRAME_TYPE_CONFIG: Record<CandidateLanguageType, {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  description: string;
}> = {
  problem: {
    label: 'Problem',
    icon: AlertCircle,
    color: `${CATEGORY_BADGE.problem.text} ${CATEGORY_BADGE.problem.bg} ${CATEGORY_BADGE.problem.border}`,
    description: 'Something that needs fixing',
  },
  opportunity: {
    label: 'Opportunity',
    icon: Lightbulb,
    color: `${CATEGORY_BADGE.opportunity.text} ${CATEGORY_BADGE.opportunity.bg} ${CATEGORY_BADGE.opportunity.border}`,
    description: 'Something we could gain',
  },
  risk: {
    label: 'Risk',
    icon: Shield,
    color: `${CATEGORY_BADGE.risk.text} ${CATEGORY_BADGE.risk.bg} ${CATEGORY_BADGE.risk.border}`,
    description: 'Something that might go wrong',
  },
  solution: {
    label: 'Solution',
    icon: Hammer,
    color: `${CATEGORY_BADGE.solution.text} ${CATEGORY_BADGE.solution.bg} ${CATEGORY_BADGE.solution.border}`,
    description: 'A specific approach',
  },
  commitment: {
    label: 'Commitment',
    icon: Handshake,
    color: `${CATEGORY_BADGE.commitment.text} ${CATEGORY_BADGE.commitment.bg} ${CATEGORY_BADGE.commitment.border}`,
    description: 'An external promise',
  },
  investment: {
    label: 'Investment',
    icon: TrendingUp,
    color: `${CATEGORY_BADGE.investment.text} ${CATEGORY_BADGE.investment.bg} ${CATEGORY_BADGE.investment.border}`,
    description: 'A strategic bet',
  },
};

const VALID_FRAME_TYPES = Object.keys(FRAME_TYPE_CONFIG) as CandidateLanguageType[];

type FrameConfig = (typeof FRAME_TYPE_CONFIG)[CandidateLanguageType];

function getFrameConfig(type: string | undefined): FrameConfig | null {
  if (!type || !VALID_FRAME_TYPES.includes(type as CandidateLanguageType)) return null;
  return FRAME_TYPE_CONFIG[type as CandidateLanguageType] ?? null;
}

const FALLBACK_FRAME_CONFIG: FrameConfig = {
  label: 'Other',
  icon: RefreshCw,
  color: 'text-[var(--text-sec)] bg-[var(--surface)] border-[var(--rule)]',
  description: 'Other category',
};

export function ItemFrameSelector({
  candidateId,
  onCreateFrame,
  onEditFrame,
  compact = false,
}: ItemFrameSelectorProps) {
  // Select raw candidate data - avoid calling functions in selectors
  const candidate = useItemsStore(state => 
    state.items.find(c => c.id === candidateId)
  );
  const setCanonicalFrame = useItemsStore(state => state.setCanonicalFrame);
  
  const setUseItemAsCanonical = useItemsStore(state => state.setUseItemAsCanonical);

  // Derive frames and canonical frame outside selector with useMemo
  const frames = useMemo(() => candidate?.frames || [], [candidate?.frames]);
  const canonicalFrame = useMemo(() => {
    if (!candidate?.frames || !candidate.canonicalFrameId) return undefined;
    return candidate.frames.find(f => f.id === candidate.canonicalFrameId);
  }, [candidate?.frames, candidate?.canonicalFrameId]);

  // Item's own framing (primary type + title) is the "original" – show it first, grayed so it can't be overwritten
  const hasOriginal = Boolean(candidate?.itemCategory);
  const isItemCanonical = !candidate?.canonicalFrameId;

  const handleSelectFrame = (frameId: string) => {
    setCanonicalFrame(candidateId, frameId);
  };

  const handleSelectOriginal = () => {
    setUseItemAsCanonical(candidateId);
  };

  if (!hasOriginal && frames.length === 0) {
    return (
      <div className="text-sm text-[var(--text-ter)] flex items-center gap-2">
        <RefreshCw className="w-4 h-4" />
        <span>No additional categories</span>
        {onCreateFrame && (
          <button
            onClick={onCreateFrame}
            className="text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:underline"
          >
            Create one
          </button>
        )}
      </div>
    );
  }

  if (compact) {
    const originalConfigCompact = hasOriginal ? getFrameConfig(candidate!.itemCategory!) : null;
    const OriginalIconCompact = originalConfigCompact?.icon ?? RefreshCw;

    // Alternate frames sorted: canonical first, then the rest.
    // Filter out frames whose frameType matches the item's own itemCategory —
    // those are already represented by the "original" pill below and would appear as duplicates.
    const itemCategory = candidate?.itemCategory;
    const sortedFrames = [...frames]
      .filter(f => {
        const ft = f.frameType ?? (f as { frame_type?: string }).frame_type;
        return ft !== itemCategory;
      })
      .sort((a, b) => {
        const aActive = a.id === canonicalFrame?.id ? 0 : 1;
        const bActive = b.id === canonicalFrame?.id ? 0 : 1;
        return aActive - bActive;
      });

    const itemOwnPill = hasOriginal ? (
      <button
        key="__item-own__"
        type="button"
        onClick={handleSelectOriginal}
        title={`Item category: ${candidate!.title}`}
        className={`inline-flex items-center gap-1.5 px-2 py-1  border text-sm transition-all ${
          isItemCanonical
            ? `${originalConfigCompact?.color ?? 'bg-[var(--hover-tint)] border-[var(--rule)] text-[var(--text-sec)]'} ring-2 ring-offset-1 ring-current`
            : 'bg-[var(--hover-tint)] border-[var(--rule)] text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)]'
        }`}
      >
        <OriginalIconCompact className="w-3.5 h-3.5" />
        <span>{originalConfigCompact?.label ?? 'Primary type not set'}</span>
        {isItemCanonical
          ? <><span className="text-[10px]">(active)</span><Check className="w-3 h-3" /></>
          : <span className="text-[10px]">(original)</span>
        }
      </button>
    ) : null;

    return (
      <div className="flex flex-wrap gap-2">
        {/* Active pill first: either the item's own category or an alternate frame */}
        {isItemCanonical && itemOwnPill}

        {sortedFrames.map(frame => {
          const config = getFrameConfig(frame.frameType) ?? FALLBACK_FRAME_CONFIG;
          const Icon = config.icon;
          const isCanonical = frame.id === canonicalFrame?.id;

          return (
            <button
              key={frame.id}
              onClick={() => {
                if (onEditFrame) {
                  onEditFrame(frame.id);
                } else {
                  handleSelectFrame(frame.id);
                }
              }}
              className={`inline-flex items-center gap-1.5 px-2 py-1  border text-sm transition-all ${
                isCanonical
                  ? `${config.color} ring-2 ring-offset-1 ring-current`
                  : `${config.color} opacity-60 hover:opacity-100`
              }`}
              title={`${config.label}: ${frame.title}`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{config.label}</span>
              {isCanonical
                ? <><span className="text-[10px]">(active)</span><Check className="w-3 h-3" /></>
                : null
              }
            </button>
          );
        })}

        {/* Item's own category pill shown after active alt frame when it's not canonical */}
        {!isItemCanonical && itemOwnPill}

        {onCreateFrame && (
          <button
            onClick={onCreateFrame}
            className="inline-flex items-center gap-1 px-2 py-1 border border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] text-sm"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        )}
      </div>
    );
  }

  const originalConfig = hasOriginal ? getFrameConfig(candidate!.itemCategory!) : null;
  const OriginalIcon = originalConfig?.icon ?? RefreshCw;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-[var(--text-sec)]">Item Categories</h4>
        {onCreateFrame && (
          <button
            onClick={onCreateFrame}
            className="inline-flex items-center gap-1 text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)]"
          >
            <Plus className="w-4 h-4" />
            Add Category
          </button>
        )}
      </div>

      <div className="grid gap-2">
        {hasOriginal && (
          <button
            type="button"
            onClick={handleSelectOriginal}
            className={`w-full text-left p-3  border transition-all ${
              isItemCanonical
                ? 'bg-[var(--hover-tint)] border-[var(--rule)] ring-2 ring-gray-400 dark:ring-gray-500'
                : 'bg-[var(--surface)] border-[var(--rule)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-sec)]'
            }`}
            title="The item's own category – cannot be overwritten"
          >
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded bg-[var(--hover-tint)]">
                <OriginalIcon className="w-4 h-4 text-[var(--text-ter)]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-[var(--text-sec)]">
                    {originalConfig?.label ?? 'Primary type not set'}
                  </span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 text-[var(--mustard)] dark:text-[var(--mustard)] font-medium" title="This was the item's original classification">
                    Original
                  </span>
                  {isItemCanonical && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--green-dim)] dark:bg-[var(--green)]/30 text-[var(--green)] dark:text-[var(--text-ter)]">
                      Active
                    </span>
                  )}
                </div>
                <p className="text-sm text-[var(--text-sec)] truncate mt-0.5">
                  {candidate!.title}
                </p>
              </div>
            </div>
          </button>
        )}
        {frames.map(frame => {
          const config = getFrameConfig(frame.frameType) ?? FALLBACK_FRAME_CONFIG;
          const Icon = config.icon;
          const isCanonical = frame.id === canonicalFrame?.id;

          return (
            <button
              key={frame.id}
              onClick={() => {
                if (onEditFrame) {
                  onEditFrame(frame.id);
                } else {
                  handleSelectFrame(frame.id);
                }
              }}
              className={`w-full text-left p-3  border transition-all ${
                isCanonical
                  ? `${config.color} ring-2 ring-current`
                  : `${config.color} opacity-60 hover:opacity-100`
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-1.5 rounded ${isCanonical ? '' : 'bg-[var(--hover-tint)]'}`}>
                  <Icon className={`w-4 h-4 ${isCanonical ? '' : 'text-[var(--text-ter)]'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{config.label}</span>
                    {frame.isOriginal && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 text-[var(--mustard)] dark:text-[var(--mustard)]" title="Original idea">
                        Original
                      </span>
                    )}
                    {isCanonical && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--green-dim)] dark:bg-[var(--green)]/30 text-[var(--green)] dark:text-[var(--text-ter)]">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--text-sec)] truncate mt-0.5">
                    {frame.title}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Badge showing the current frame type (candidate type = frame type).
 */
export function FrameTypeBadge({ 
  frameType,
  size = 'md',
}: { 
  frameType: CandidateLanguageType | string;
  size?: 'sm' | 'md';
}) {
  const config = getFrameConfig(frameType) ?? FALLBACK_FRAME_CONFIG;
  const Icon = config.icon;

  const sizeClasses = size === 'sm' 
    ? 'px-1.5 py-0.5 text-xs'
    : 'px-2 py-1 text-sm';

  return (
    <span 
      className={`inline-flex items-center gap-1 rounded-full ${config.color} ${sizeClasses}`}
      title={config.description}
    >
      <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
      {config.label}
    </span>
  );
}

export default ItemFrameSelector;
